import React, { useState } from 'react';
import styled from 'styled-components';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button, Input, Text } from '@teals/ui-web';
import { useAuth } from '../../hooks';
import { useLinkGoogleMutation } from '../../store/api/portalApi';
import { hydrateCognitoSession, refreshSession, setSsoFlow } from '../../services/cognito';
import { completeSsoLink } from '../../services/sso';
import { setCredentials } from '../../store/slices';
import { useAppDispatch } from '../../store';
import { normalizeAuthEmail } from '../../utils/ssoLinkEmail';

const Container = styled.div`
  width: 100%;
  max-width: 380px;
`;

const Lead = styled.p`
  margin: 0 0 1.5rem;
  color: var(--text-secondary);
  font-size: 0.875rem;
  line-height: 1.5;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const ErrorMsg = styled.div`
  padding: 0.75rem 1rem;
  background: var(--error-subtle);
  color: var(--error);
  border-radius: 8px;
  font-size: 0.875rem;
`;

const Secondary = styled.button`
  background: none;
  border: none;
  color: var(--accent);
  font-size: 0.875rem;
  cursor: pointer;
  text-align: left;
  padding: 0;

  &:hover {
    color: var(--accent-hover);
  }
`;

const LinkAccounts: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { login, isLoading, error, user } = useAuth();
  const [linkGoogle, { isLoading: isLinking }] = useLinkGoogleMutation();
  const emailFromQuery = normalizeAuthEmail(
    new URLSearchParams(location.search).get('email') || ''
  );
  const linkState =
    location.state && typeof location.state === 'object'
      ? (location.state as {
          email?: string;
          pending_link_id?: string;
          provider?: string;
        })
      : {};
  const emailFromState = normalizeAuthEmail(linkState.email || '');
  const pendingLinkId = String(linkState.pending_link_id || '');
  const provider = String(linkState.provider || 'google');
  const [email, setEmail] = useState(emailFromState || emailFromQuery);
  const [password, setPassword] = useState('');
  const [formError, setFormError] = useState('');

  const applySession = async (result: {
    user: {
      userId: string;
      email: string;
      name: string;
      organizationId?: string;
      groups: string[];
    };
    tokens: { idToken: string; accessToken: string; refreshToken: string };
  }) => {
    setSsoFlow('linked');
    dispatch(
      setCredentials({
        user: {
          id: result.user.userId,
          email: result.user.email,
          name: result.user.name,
          organizationId: result.user.organizationId,
          groups: result.user.groups,
        },
        token: result.tokens.idToken,
        accessToken: result.tokens.accessToken,
        refreshToken: result.tokens.refreshToken,
      })
    );
    navigate('/agents', { replace: true });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    const normalizedEmail = normalizeAuthEmail(email) || email;
    if (pendingLinkId) {
      try {
        const outcome = await completeSsoLink({
          pending_link_id: pendingLinkId,
          provider,
          email: normalizedEmail,
          password,
        });
        if (outcome.status !== 'signed_in' || !outcome.tokens) {
          setFormError('Could not link this identity to the account.');
          return;
        }
        const session = hydrateCognitoSession(
          outcome.tokens.id_token,
          outcome.tokens.access_token,
          outcome.tokens.refresh_token
        );
        await applySession(session);
      } catch (err: unknown) {
        setFormError(
          err instanceof Error
            ? err.message
            : 'Could not link this identity to the account.'
        );
      }
      return;
    }
    const signedIn = await login(normalizedEmail, password);
    if (signedIn === false) {
      return;
    }
    try {
      await linkGoogle().unwrap();
      const refreshed = await refreshSession();
      if (refreshed) {
        await applySession(refreshed);
      } else if (user) {
        navigate('/agents', { replace: true });
      }
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'data' in err
          ? String(
              (err as { data?: { message?: string } }).data?.message ||
                'Could not link Google to this account.'
            )
          : err instanceof Error
            ? err.message
            : 'Could not link Google to this account.';
      setFormError(message);
    }
  };

  return (
    <Container>
      <Text variant="h3" style={{ marginBottom: '0.5rem' }}>
        Link your accounts
      </Text>
      <Lead>
        An account with this email already exists. Enter your Luminafactory password
        to link Google. You can keep signing in with either method.
      </Lead>
      {(formError || error) && <ErrorMsg>{formError || error}</ErrorMsg>}
      <Form onSubmit={handleSubmit}>
        <Input
          label="Email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          readOnly={Boolean(emailFromQuery || emailFromState)}
          fullWidth
          required
        />
        <Input
          label="Luminafactory password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Enter your Luminafactory password"
          autoComplete="current-password"
          fullWidth
          required
        />
        <Button type="submit" fullWidth isLoading={isLoading || isLinking}>
          Link accounts
        </Button>
        <Secondary
          type="button"
          onClick={() => {
            setSsoFlow('');
            navigate('/auth');
          }}
        >
          Use a different email
        </Secondary>
      </Form>
    </Container>
  );
};

export default LinkAccounts;
